// ✅ UPDATED FILE: src/stores/forgotPasswordStore.js
// - Separate RESEND endpoint (no new OTP created)
// - Accept-Language explicitly (best practice)
// - Handles cooldown (429) + missing/expired (422) cleanly
// - Always uses integer seconds (no decimals)
// - Prevents "timer ended but backend still cooldown" by syncing to backend retry_after

import { defineStore } from "pinia";
import api from "@/services/api";

function getLang() {
  const stored = localStorage.getItem("app_lang");
  const htmlLang = document.documentElement.lang;
  const lang = (stored || htmlLang || "ar").toLowerCase();
  return lang.startsWith("en") ? "en" : "ar";
}

function fallbackMsg() {
  return getLang() === "en"
    ? "Something went wrong. Please try again."
    : "حدث خطأ غير متوقع. حاول مرة أخرى.";
}

function pickMessage(payload) {
  if (!payload) return fallbackMsg();
  if (typeof payload === "string") return payload;

  return (
    payload.message ||
    payload.error ||
    payload.detail ||
    payload.msg ||
    (Array.isArray(payload.messages) ? payload.messages?.[0] : null) ||
    fallbackMsg()
  );
}

function normalizeErrors(payload) {
  if (!payload) return { message: fallbackMsg() };
  if (typeof payload === "string") return { message: payload };

  const fieldErrors = payload.errors || payload.data?.errors || {};
  const message = pickMessage(payload);

  return { message, ...fieldErrors };
}

function safeRetryAfter(err) {
  // backend might return { retry_after } or header "Retry-After"
  const body = err?.response?.data;
  const raBody = Number(body?.retry_after);
  const raHeader = Number(err?.response?.headers?.["retry-after"]);
  if (Number.isFinite(raBody) && raBody > 0) return raBody;
  if (Number.isFinite(raHeader) && raHeader > 0) return raHeader;
  return null;
}

function toIntSeconds(val, fallback = 60) {
  const n = Number(val);
  if (!Number.isFinite(n) || n <= 0) return fallback;
  // ✅ avoid decimals: show correct remaining time
  return Math.max(1, Math.ceil(n));
}

export const useForgotPasswordStore = defineStore("forgotPassword", {
  state: () => ({
    loading: false,

    // flow
    channel: "email", // email | phone
    identifier: "",

    // verification
    token: "",
    verified: false,

    // UI helpers
    timer: 0,
    lastRequestedAt: null,
    _timerId: null,

    // errors
    errors: {},
  }),

  getters: {
    canResend: (s) => s.timer <= 0 && !s.loading && !!s.identifier,
  },

  actions: {
    clearErrors() {
      this.errors = {};
    },

    setChannel(ch) {
      this.channel = ch || "email";
      this.identifier = "";
      this.verified = false;
      this.token = "";
      this.clearErrors();
      this._stopTimer();
    },

    _stopTimer() {
      if (this._timerId) clearInterval(this._timerId);
      this._timerId = null;
      this.timer = 0;
    },

    startTimer(seconds = 60) {
      this._stopTimer();

      this.timer = toIntSeconds(seconds, 60);
      if (this.timer <= 0) return;

      this._timerId = setInterval(() => {
        this.timer = Math.max(0, this.timer - 1);
        if (this.timer <= 0) this._stopTimer();
      }, 1000);
    },

    async requestCode({ channel, identifier }) {
      this.loading = true;
      this.clearErrors();

      try {
        const payload = {
          channel: channel ?? this.channel,
          identifier: identifier ?? this.identifier,
        };

        const res = await api.post("/forgot-password", payload, {
          headers: { "Accept-Language": getLang() },
        });

        // keep store synced
        this.channel = payload.channel;
        this.identifier = payload.identifier;
        this.verified = false;
        this.token = "";
        try {
          localStorage.removeItem("temp_token");
        } catch {
          // ignore storage errors
        }

        this.lastRequestedAt = Date.now();

        const tempToken =
          res?.data?.token || res?.data?.data?.token || "";
        if (tempToken) {
          try {
            localStorage.setItem("temp_token", tempToken);
          } catch {
            // ignore storage errors
          }
        }

        const retryAfter =
          res?.data?.retry_after ??
          res?.data?.data?.retry_after ??
          60;

        this.startTimer(retryAfter);

        return res.data;
      } catch (err) {
        this.errors = normalizeErrors(err?.response?.data);
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async verifyResetCode({ code, identifier }) {
      this.loading = true;
      this.clearErrors();

      try {
        const payload = {
          code,
          identifier: identifier ?? this.identifier,
        };

        let tempToken = "";
        try {
          tempToken = localStorage.getItem("temp_token") || "";
        } catch {
          // ignore storage errors
        }

        const res = await api.post("/verify-reset-code", payload, {
          headers: {
            "Accept-Language": getLang(),
            ...(tempToken ? { Authorization: `Bearer ${tempToken}` } : {}),
          },
        });

        this.token = res?.data?.token || res?.data?.data?.token || "";
        this.verified = !!this.token;

        this.identifier = payload.identifier;
        if (this.token) {
          try {
            localStorage.setItem("temp_token", this.token);
          } catch {
            // ignore storage errors
          }
        }

        return res.data;
      } catch (err) {
        this.errors = normalizeErrors(err?.response?.data);
        this.verified = false;
        this.token = "";
        try {
          localStorage.removeItem("temp_token");
        } catch {
          // ignore storage errors
        }
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async resetPassword({ password, password_confirmation, identifier, token }) {
      this.loading = true;
      this.clearErrors();

      try {
        let storedToken = "";
        try {
          storedToken = localStorage.getItem("temp_token") || "";
        } catch {
          // ignore storage errors
        }
        const payload = {
          identifier: identifier ?? this.identifier,
          token: token ?? this.token ?? storedToken,
          password,
          password_confirmation,
        };

        const res = await api.post("/reset-password", payload, {
          headers: { "Accept-Language": getLang() },
        });

        // reset store state (optional)
        this.verified = false;
        this.token = "";
        this._stopTimer();
        try {
          localStorage.removeItem("temp_token");
        } catch {
          // ignore storage errors
        }

        return res.data;
      } catch (err) {
        this.errors = normalizeErrors(err?.response?.data);
        throw err;
      } finally {
        this.loading = false;
      }
    },

    /**
     * ✅ Separate resend endpoint (does NOT create a new OTP)
     * POST /resend-verification
     *
     * - 429 => cooldown, backend returns retry_after (remaining seconds)
     * - 422 => missing/expired, UI should go back to step 1
     */
    async resend() {
      if (!this.canResend) return;

      this.loading = true;
      this.clearErrors();

      try {
        const payload = {
          channel: this.channel,
          identifier: this.identifier,
        };

        const res = await api.post("/resend-verification", payload, {
          headers: { "Accept-Language": getLang() },
        });

        const retryAfter =
          res?.data?.retry_after ??
          res?.data?.data?.retry_after ??
          60;

        this.startTimer(retryAfter);

        return res.data;
      } catch (err) {
        const status = err?.response?.status;

        // ✅ Cooldown => sync UI with backend remaining seconds
        if (status === 429) {
          const ra = safeRetryAfter(err) ?? 60;
          this.startTimer(ra);

          this.errors = normalizeErrors(err?.response?.data);
          throw err;
        }

        // ✅ Missing/expired => keep errors; UI can switch to step 1
        if (status === 422) {
          this.errors = normalizeErrors(err?.response?.data);
          // stop timer because resend isn't valid anymore (optional)
          this._stopTimer();
          throw err;
        }

        this.errors = normalizeErrors(err?.response?.data);
        throw err;
      } finally {
        this.loading = false;
      }
    },
  },
});
